
import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';
import ScrollAnimation from '../../../components/base/ScrollAnimation';

export default function CTASection() {
  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.href = '/contact';
    setTimeout(() => {
      const element = document.getElementById('quote-section');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <section className="py-24 max-md:py-8 bg-gradient-to-br from-secondary via-gray-800 to-secondary relative overflow-hidden mobile-compact-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(45deg, transparent 48%, white 49%, white 51%, transparent 52%)',
          backgroundSize: '20px 20px',
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-md:gap-6 items-center mobile-compact-gap">
          {/* Left Content */}
          <ScrollAnimation animation="slide-in-left">
            <div className="text-white">
              <h2 className="text-4xl md:text-5xl max-md:text-2xl font-bold mb-6 max-md:mb-3 leading-tight mobile-compact-text">
                Ready to Transform Your Vehicle?
              </h2>
              <p className="text-xl max-md:text-sm text-gray-300 mb-8 max-md:mb-4 leading-relaxed mobile-compact-text">
                Get professional window tinting with premium materials, expert installation, and a lifetime warranty. Book your appointment today and experience the difference.
              </p>

              {/* Benefits List */}
              <div className="space-y-4 max-md:space-y-2 mb-8 max-md:mb-4 mobile-compact-gap">
                {[
                  'Free consultation and quote',
                  'Same-day service available',
                  'Lifetime warranty included',
                  'Premium Flex Film materials',
                  'Expert certified installers',
                  'Mobile service available',
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center space-x-3 max-md:space-x-2">
                    <div className="w-6 h-6 max-md:w-4 max-md:h-4 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm max-md:text-xs"></i>
                    </div>
                    <span className="text-lg max-md:text-xs">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 max-md:gap-2 mobile-compact-gap">
                <Link to="/contact" onClick={handleContactClick}>
                  <Button size="lg" className="min-w-[200px] max-md:min-w-[140px] max-md:text-sm max-md:py-2">
                    <i className="ri-calendar-check-line mr-2 max-md:text-sm"></i>
                    Request Appointment
                  </Button>
                </Link>
                <a href="tel:4438000348">
                  <Button size="lg" variant="outline" className="min-w-[200px] max-md:min-w-[140px] max-md:text-sm max-md:py-2 border-white text-white hover:bg-white hover:text-secondary">
                    <i className="ri-phone-line mr-2 max-md:text-sm"></i>
                    Call Us Today
                  </Button>
                </a>
              </div>
            </div>
          </ScrollAnimation>

          {/* Right Content - Contact Card */}
          <ScrollAnimation animation="slide-in-right">
            <div className="bg-white rounded-2xl max-md:rounded-lg shadow-2xl p-8 max-md:p-4">
              <h3 className="text-2xl max-md:text-lg font-bold text-secondary mb-6 max-md:mb-3">Get Your Free Quote</h3>
              
              <div className="space-y-6 max-md:space-y-3 mobile-compact-gap">
                {/* Contact Info */}
                <div className="flex items-start space-x-4 max-md:space-x-2">
                  <div className="w-12 h-12 max-md:w-8 max-md:h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl max-md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold max-md:text-sm text-secondary mb-1">Call Us</h4>
                    <a href="tel:4438000348" className="text-lg max-md:text-xs text-primary hover:text-accent transition-colors whitespace-nowrap">
                      (443) 800-0348
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 max-md:space-x-2">
                  <div className="w-12 h-12 max-md:w-8 max-md:h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl max-md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold max-md:text-sm text-secondary mb-1">Email Us</h4>
                    <a href="mailto:ubkwindowtintservices@gmail.com" className="text-primary max-md:text-xs hover:text-accent transition-colors break-all">
                      ubkwindowtintservices@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 max-md:space-x-2">
                  <div className="w-12 h-12 max-md:w-8 max-md:h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl max-md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold max-md:text-sm text-secondary mb-1">Location</h4>
                    <p className="text-gray-600 max-md:text-xs">313 Nottingham Road</p>
                    <p className="text-gray-600 max-md:text-xs">Baltimore, MD 21229</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 max-md:space-x-2">
                  <div className="w-12 h-12 max-md:w-8 max-md:h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl max-md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold max-md:text-sm text-secondary mb-1">Hours</h4>
                    <p className="text-gray-600 max-md:text-xs">Mon-Sat: 8am - 5pm</p>
                    <p className="text-gray-600 max-md:text-xs">Sun: 8am - 12pm</p>
                    <p className="text-accent font-semibold text-sm max-md:text-xs mt-1">BY APPOINTMENT ONLY</p>
                  </div>
                </div>
              </div>

              {/* Special Offer */}
              <div className="mt-8 max-md:mt-4 bg-gradient-to-r from-primary to-accent rounded-xl max-md:rounded-lg p-6 max-md:p-3 text-white text-center mobile-compact-spacing">
                <h4 className="text-xl max-md:text-sm font-bold mb-2 max-md:mb-1">Special Offer!</h4>
                <p className="text-lg max-md:text-xs mb-3 max-md:mb-1">Get 10% off when you mention this website</p>
                <p className="text-sm max-md:text-xs opacity-90">Limited time offer - Book today!</p>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </div>
    </section>
  );
}
